import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState, AppDispatch } from '@/store'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { fetchReminders, upsertReminder, removeReminder } from '@/store/slices/remindersSlice'
import { useReminderScheduler } from './useReminderScheduler'
import { useReminderActions } from './useReminderActions'
import { subscribeReminderEvents } from './reminderBroadcast'
import { todayISO } from './reminderTime'
import { StickyPopup } from './StickyPopup'

export function RemindersHost() {
  const dispatch = useDispatch<AppDispatch>()
  const tz = useUserTimezone()
  const items = useSelector((s: RootState) => s.reminders.items)
  const { onScreen, dismiss, togglePin, setPosition, noteResolved } = useReminderScheduler(items, tz)
  const actions = useReminderActions(tz)

  useEffect(() => { void dispatch(fetchReminders()) }, [dispatch])

  useEffect(() => {
    const refetch = () => { if (document.visibilityState === 'visible') void dispatch(fetchReminders()) }
    document.addEventListener('visibilitychange', refetch)
    window.addEventListener('focus', refetch)
    return () => {
      document.removeEventListener('visibilitychange', refetch)
      window.removeEventListener('focus', refetch)
    }
  }, [dispatch])

  useEffect(() => {
    return subscribeReminderEvents((e) => {
      if (e.kind === 'upserted') dispatch(upsertReminder(e.reminder))
      else if (e.kind === 'removed') dispatch(removeReminder(e.id))
      else if (e.kind === 'refetch') void dispatch(fetchReminders())
    })
  }, [dispatch])

  return (
    // column-reverse 的流式堆叠:让浏览器按卡片真实高度排,别再猜固定槽高。
    // 旧写法给每张卡算 top = vh - SLOT_H*i,SLOT_H=168 但卡片文本一长就 185+,
    // 两张便签实测重叠 4px(文本 3 行时更多)。拖过的卡自己 position:fixed 脱流,
    // 在这个流里占 0 高,不影响其余卡的堆叠。
    <div style={{
      position: 'fixed', right: 22, bottom: 20, zIndex: 1000, pointerEvents: 'none',
      display: 'flex', flexDirection: 'column-reverse', alignItems: 'flex-end', gap: 14,
    }}>
      {onScreen.map((n, i) => {
        const id = String(n.rem.id)
        return (
          <StickyPopup
            key={id}
            rem={n.rem} index={i} tz={tz} pinned={n.pinned} pos={n.pos}
            overdue={n.rem.dueDate < todayISO(tz)}
            onDone={() => { actions.toggle(n.rem.id); noteResolved(id) }}
            onSnoozeTo={(d, tm) => { actions.snoozeTo(n.rem.id, d, tm); noteResolved(id) }}
            onDismiss={() => dismiss(id)}
            onTogglePin={() => togglePin(id)}
            onDragEnd={(x, y) => setPosition(id, x, y)}
          />
        )
      })}
    </div>
  )
}
